import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLangPath } from '../../hooks/useLangPath';

// ─── Case Studies ─────────────────────────────────────────────────────────────
const CASE_STUDIES = [
  {
    id: 1,
    category: 'Restaurant Tech · AI Optimization',
    tag: 'DineOS',
    color: '#45d0bd',
    icon: '🍽️',
    headline: 'AI Cut Restaurant Operational Cost by 30%',
    subtitle: 'How WIZ turned a struggling restaurant chain into a lean, data-driven operation using AI order prediction and smart waste control.',
    challenge: 'A restaurant chain with 8 branches was losing 30% of revenue to food waste, over-staffing during off-peak hours, and inefficient ordering systems that could not anticipate demand spikes.',
    solution: 'WIZ deployed DineOS — an end-to-end smart restaurant platform featuring an AI engine trained on 2 years of sales history to predict peak hours, automate supply orders, and dynamically adjust staffing. A touch-screen kiosk system replaced manual ordering, cutting errors and wait times.',
    results: [
      { metric: '30%', label: 'Reduction in operational costs' },
      { metric: '45%', label: 'Less food waste within 3 months' },
      { metric: '3×', label: 'Faster order processing via kiosks' },
      { metric: '8', label: 'Branches fully digitized' },
    ],
    features: [
      'AI-powered demand forecasting',
      'Touch-screen self-ordering kiosks',
      'Kitchen Display System (KDS)',
      'Real-time inventory & waste tracking',
      'Integrated e-payment gateway',
      'Manager analytics dashboard',
    ],
    technologies: ['Android', 'Laravel', 'Vue.js', 'AI/ML', 'POS Integration', 'REST API'],
    link: 'https://pos.wiz-tech.co',
    images: [
      '/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.29.png',
      '/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.32.png',
    ],
    quote: "WIZ didn't just build us software — they transformed how we run our entire business.",
    quotePerson: 'Restaurant Operations Manager',
  },
  {
    id: 2,
    category: 'HealthTech · Clinical Platform',
    tag: 'Medical Platform',
    color: '#8258c8',
    icon: '🏥',
    headline: 'Zero-Error Hospital Platform Built for Clinical Precision',
    subtitle: 'How WIZ engineered a medical-grade platform that eliminated data-entry errors and digitized an entire hospital\'s workflow from appointments to follow-ups.',
    challenge: 'A regional hospital was running on paper-based records and disconnected spreadsheets. Appointment collisions, lost patient files, and medication errors caused by manual data entry were affecting patient care quality.',
    solution: 'WIZ built a full-stack healthcare platform covering patient management, appointment scheduling with collision prevention, structured medical record storage, and a doctor-patient content portal. Every data entry point has validation layers that make errors structurally impossible.',
    results: [
      { metric: '0', label: 'Appointment collisions since launch' },
      { metric: '100%', label: 'Digital patient record coverage' },
      { metric: '60%', label: 'Reduction in admin overhead' },
      { metric: '4.9★', label: 'Average clinic staff satisfaction' },
    ],
    features: [
      'Collision-proof appointment engine',
      'Structured patient medical records',
      'Doctor content publishing system',
      'Patient mobile app (iOS & Android)',
      'Role-based access control',
      'Real-time follow-up notifications',
    ],
    technologies: ['Flutter', 'Laravel', 'Firebase', 'REST API', 'MySQL', 'Push Notifications'],
    link: 'https://qlickhealth.ai',
    images: [
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.11.38.png',
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.29.48.png',
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.12.10.png',
    ],
    quote: 'Before WIZ, we had data chaos. Now the system protects our patients by making errors impossible.',
    quotePerson: 'Hospital IT Director',
  },
  {
    id: 3,
    category: 'AI · Robotics · HealthTech',
    tag: 'Autism Therapy Robot',
    color: '#2c84c8',
    icon: '🤖',
    headline: 'The Robot That Changed Everything in Autism Therapy',
    subtitle: 'How WIZ built an AI-powered therapy robot that personalizes autism treatment in real-time — enabling breakthroughs that human-only therapy couldn\'t achieve alone.',
    challenge: 'Autism therapy centers faced a critical shortage of qualified therapists. Sessions were inconsistent, non-personalized, and expensive. Children on the spectrum needed structured, repetitive, and patient-adaptive interactions that human therapists couldn\'t always sustain at scale.',
    solution: 'WIZ engineered a therapy robot system with embedded AI that runs structured, adaptive therapy programs. The robot responds to the child\'s behavior in real time, adjusts the session intensity, and logs all interaction data to a secure dashboard for therapist review. It extends — not replaces — clinical expertise.',
    results: [
      { metric: '3×', label: 'More therapy sessions per day per child' },
      { metric: '82%', label: 'Of children showed measurable progress' },
      { metric: '60%', label: 'Reduction in therapist fatigue & burnout' },
      { metric: '100%', label: 'Secure, HIPAA-compliant data logging' },
    ],
    features: [
      'Real-time behavior-adaptive therapy programs',
      'Child engagement & response tracking',
      'Therapist review dashboard',
      'Secure session data logging',
      'Remote monitoring capability',
      'Multi-program therapy library',
    ],
    technologies: ['Embedded Systems', 'AI/ML', 'Python', 'Database', 'IoT', 'Real-time Processing'],
    link: 'https://qlickhealth.ai',
    images: [
      '/assets/images/robot/190610_NB1_Rasiing Hand_with Ear_Ted-1.png',
    ],
    quote: 'For the first time, our kids had a tireless, consistent companion that truly adapted to them.',
    quotePerson: 'Autism Therapy Center Director',
  },
];

const CASE_STUDIES_AR = [
  {
    id: 1,
    category: 'تقنية المطاعم · تحسين بالذكاء الاصطناعي',
    tag: 'DineOS',
    color: '#45d0bd',
    icon: '🍽️',
    headline: 'الذكاء الاصطناعي يخفض التكاليف التشغيلية للمطاعم بنسبة 30%',
    subtitle: 'كيف حوّلت WIZ سلسلة مطاعم تعاني من خسائر إلى عملية ذكية تعتمد على البيانات باستخدام توقع الطلبات بالذكاء الاصطناعي والتحكم الذكي في الهدر.',
    challenge: 'كانت سلسلة مطاعم بـ 8 فروع تخسر 30٪ من إيراداتها بسبب هدر الطعام والإفراط في التوظيف في ساعات الهدوء وأنظمة طلب غير قادرة على توقع ارتفاعات الطلب.',
    solution: 'نشرت WIZ نظام DineOS — منصة مطاعم ذكية متكاملة تضم محرك ذكاء اصطناعي مدرّب على سنتين من بيانات المبيعات لتوقع ساعات الذروة وأتمتة طلبات التوريد وضبط الكوادر البشرية ديناميكيًا. حلّت أكشاك اللمس محل الطلب اليدوي مما قلّل الأخطاء وأوقات الانتظار.',
    results: [
      { metric: '30%', label: 'انخفاض في التكاليف التشغيلية' },
      { metric: '45%', label: 'تقليل هدر الطعام خلال 3 أشهر' },
      { metric: '3×', label: 'تسريع معالجة الطلبات عبر الأكشاك' },
      { metric: '8', label: 'فروع رقمنت بالكامل' },
    ],
    features: [
      'توقع الطلب بالذكاء الاصطناعي',
      'أكشاك طلب ذاتي بشاشة لمس',
      'نظام عرض المطبخ (KDS)',
      'تتبع المخزون والهدر بشكل فوري',
      'بوابة دفع إلكتروني متكاملة',
      'لوحة تحليلات للمدير',
    ],
    technologies: ['Android', 'Laravel', 'Vue.js', 'AI/ML', 'POS Integration', 'REST API'],
    link: 'https://pos.wiz-tech.co',
    images: [
      '/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.29.png',
      '/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.32.png',
    ],
    quote: 'لم تبنِ WIZ برنامجًا فحسب — بل غيّرت طريقة إدارتنا لأعمالنا بالكامل.',
    quotePerson: 'مدير العمليات في المطعم',
  },
  {
    id: 2,
    category: 'تقنية الصحة · المنصة السريرية',
    tag: 'المنصة الطبية',
    color: '#8258c8',
    icon: '🏥',
    headline: 'منصة مستشفى بلا أخطاء بُنيت للدقة الطبية',
    subtitle: 'كيف هندست WIZ منصة طبية بدرجة سريرية أزالت أخطاء إدخال البيانات ورقمنت سير عمل مستشفى بأكمله من المواعيد إلى المتابعات.',
    challenge: 'كان مستشفى إقليمي يعمل على سجلات ورقية وجداول بيانات منفصلة. كانت تعارضات المواعيد وملفات المرضى الضائعة وأخطاء الأدوية الناجمة عن الإدخال اليدوي تؤثر على جودة رعاية المرضى.',
    solution: 'بنت WIZ منصة رعاية صحية متكاملة تشمل إدارة المرضى وجدولة المواعيد مع منع التعارض وتخزين السجلات الطبية المنظمة وبوابة محتوى بين الطبيب والمريض. لكل نقطة إدخال بيانات طبقات تحقق تجعل الأخطاء مستحيلة هيكليًا.',
    results: [
      { metric: '0', label: 'تعارضات مواعيد منذ الإطلاق' },
      { metric: '100%', label: 'تغطية رقمية لسجلات المرضى' },
      { metric: '60%', label: 'تقليل الأعباء الإدارية' },
      { metric: '4.9★', label: 'متوسط رضا موظفي العيادة' },
    ],
    features: [
      'محرك مواعيد يمنع التعارض',
      'سجلات طبية منظمة للمرضى',
      'نظام نشر محتوى طبي للأطباء',
      'تطبيق جوال للمرضى (iOS وAndroid)',
      'تحكم في الوصول حسب الدور',
      'إشعارات متابعة فورية',
    ],
    technologies: ['Flutter', 'Laravel', 'Firebase', 'REST API', 'MySQL', 'Push Notifications'],
    link: 'https://qlickhealth.ai',
    images: [
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.11.38.png',
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.29.48.png',
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.12.10.png',
    ],
    quote: 'قبل WIZ كان لدينا فوضى في البيانات. الآن النظام يحمي مرضانا بجعل الأخطاء مستحيلة.',
    quotePerson: 'مدير تقنية المعلومات في المستشفى',
  },
  {
    id: 3,
    category: 'ذكاء اصطناعي · روبوتات · تقنية صحية',
    tag: 'روبوت علاج التوحد',
    color: '#2c84c8',
    icon: '🤖',
    headline: 'الروبوت الذي غيّر كل شيء في علاج التوحد',
    subtitle: 'كيف بنت WIZ روبوت علاج مدعومًا بالذكاء الاصطناعي يخصّص علاج التوحد في الوقت الفعلي — محققًا اختراقات لم يكن العلاج البشري وحده قادرًا على تحقيقها.',
    challenge: 'واجهت مراكز علاج التوحد نقصًا حادًا في المعالجين المؤهلين. كانت الجلسات غير منتظمة وغير مخصصة ومكلفة. احتاج الأطفال المصابون بالتوحد إلى تفاعلات منظمة ومتكررة ومتكيفة مع المريض لا يستطيع المعالجون البشريون تقديمها دائمًا على نطاق واسع.',
    solution: 'هندست WIZ نظام روبوت علاجي مع ذكاء اصطناعي مدمج يدير برامج علاجية منظمة ومتكيفة. يستجيب الروبوت لسلوك الطفل في الوقت الفعلي ويضبط كثافة الجلسة ويسجل جميع بيانات التفاعل في لوحة آمنة لمراجعة المعالج. يمتد — ولا يستبدل — الخبرة السريرية.',
    results: [
      { metric: '3×', label: 'جلسات علاج أكثر يوميًا لكل طفل' },
      { metric: '82%', label: 'من الأطفال أظهروا تقدمًا ملموسًا' },
      { metric: '60%', label: 'تقليل إرهاق المعالجين واحتراقهم' },
      { metric: '100%', label: 'تسجيل بيانات آمن ومتوافق مع HIPAA' },
    ],
    features: [
      'برامج علاج تتكيف مع السلوك فوريًا',
      'تتبع تفاعل الطفل واستجابته',
      'لوحة مراجعة للمعالج',
      'تسجيل آمن لبيانات الجلسة',
      'إمكانية المراقبة عن بُعد',
      'مكتبة برامج علاجية متعددة',
    ],
    technologies: ['Embedded Systems', 'AI/ML', 'Python', 'Database', 'IoT', 'Real-time Processing'],
    link: 'https://qlickhealth.ai',
    images: [
      '/assets/images/robot/190610_NB1_Rasiing Hand_with Ear_Ted-1.png',
    ],
    quote: 'للمرة الأولى، كان لأطفالنا رفيق لا يكلّ ومنتظم ويتكيف معهم حقًا.',
    quotePerson: 'مدير مركز علاج التوحد',
  },
];

const glass = {
  background: 'rgba(10, 10, 18, 0.58)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: '20px',
};

const CaseStudies = () => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const getPath = useLangPath();
  const caseStudies = isAr ? CASE_STUDIES_AR : CASE_STUDIES;
  const [active, setActive] = useState(0);

  const study = caseStudies[active];

  return (
    <>
      {/* ── Hero ── */}
      <section style={{ padding: '170px 0 60px', textAlign: 'center', background: 'transparent' }}>
        <div className="container">
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: 'rgba(130,88,200,0.12)', border: '1px solid rgba(130,88,200,0.3)',
            borderRadius: '50px', padding: '6px 18px', marginBottom: '24px',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#8258c8', display: 'inline-block' }} />
            <span style={{ color: '#8258c8', fontSize: '13px', fontWeight: 600 }}>{isAr ? 'قصص النجاح' : 'Impact Stories'}</span>
          </div>
          <h1 style={{ color: '#fff', fontSize: 'clamp(2.2rem,5vw,3.6rem)', fontWeight: 700, lineHeight: 1.2, marginBottom: 18 }}>
            {isAr ? <><span style={{ background: 'linear-gradient(102deg, #8258c8, #2c84c8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>نتائج حقيقية</span>، عملاء حقيقيون</> : <>Real <span style={{ background: 'linear-gradient(102deg, #8258c8, #2c84c8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Results</span>, Real Clients</>}
          </h1>
          <p style={{ color: '#6b6b88', maxWidth: '580px', margin: '0 auto', fontSize: '1.05rem', lineHeight: 1.7 }}>
            {isAr ? 'ثلاثة تحولات تُظهر ما يحدث عندما تلتقي برامج WIZ المهندسة بتحدي أعمال حقيقي.' : 'Three transformations that show what happens when WIZ-engineered software meets a real business challenge.'}
          </p>
        </div>
      </section>

      {/* ── Case Study Tabs ── */}
      <section style={{ background: 'transparent', padding: '0 0 30px' }}>
        <div className="container">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            {caseStudies.map((cs, i) => (
              <button
                key={cs.id}
                onClick={() => setActive(i)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 24px', borderRadius: 50, cursor: 'pointer',
                  border: 'none', transition: 'all 0.3s',
                  background: active === i
                    ? `linear-gradient(102deg, ${cs.color}cc, ${cs.color}88)`
                    : 'rgba(255,255,255,0.06)',
                  color: active === i ? '#fff' : 'rgba(255,255,255,0.55)',
                  boxShadow: active === i ? `0 8px 24px ${cs.color}35` : 'none',
                  border: active === i ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  fontWeight: 700, fontSize: 14,
                }}
              >
                <span>{cs.icon}</span>
                <span>{cs.tag}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Active Case Study ── */}
      <section style={{ background: 'transparent', padding: '20px 0 80px' }}>
        <div className="container">
          {/* Header */}
          <div style={{ marginBottom: 48 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: `${study.color}15`, border: `1px solid ${study.color}35`,
              borderRadius: 50, padding: '6px 16px', marginBottom: 20,
            }}>
              <span>{study.icon}</span>
              <span style={{ color: study.color, fontSize: 12, fontWeight: 700 }}>{study.category}</span>
            </div>
            <h2 style={{
              color: '#fff', fontSize: 'clamp(1.8rem, 4vw, 3rem)',
              fontWeight: 800, lineHeight: 1.2, marginBottom: 16, maxWidth: 800,
            }}>
              {study.headline}
            </h2>
            <p style={{ color: '#8888aa', fontSize: '1.1rem', lineHeight: 1.7, maxWidth: 720 }}>
              {study.subtitle}
            </p>
          </div>

          {/* Results metrics */}
          <div className="row g-3" style={{ marginBottom: 48 }}>
            {study.results.map((r, i) => (
              <div className="col-6 col-md-3" key={i}>
                <div style={{
                  ...glass, padding: '28px 20px', textAlign: 'center',
                  borderTop: `3px solid ${study.color}`,
                }}>
                  <div style={{
                    fontSize: '2.4rem', fontWeight: 900,
                    background: `linear-gradient(135deg, ${study.color}, ${study.color}88)`,
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    marginBottom: 8,
                  }}>
                    {r.metric}
                  </div>
                  <div style={{ color: '#8888aa', fontSize: '0.85rem', lineHeight: 1.4 }}>{r.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Main content */}
          <div className="row g-4">
            {/* Left: challenge + solution */}
            <div className="col-lg-7">
              <div style={{ ...glass, padding: '32px', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: `${study.color}15`, border: `1px solid ${study.color}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={study.color} strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
                    </svg>
                  </div>
                  <h3 style={{ color: '#fff', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>{isAr ? 'التحدي' : 'The Challenge'}</h3>
                </div>
                <p style={{ color: '#8888aa', lineHeight: 1.8, margin: 0 }}>{study.challenge}</p>
              </div>

              <div style={{ ...glass, padding: '32px', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: `${study.color}15`, border: `1px solid ${study.color}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={study.color} strokeWidth="2">
                      <path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/>
                    </svg>
                  </div>
                  <h3 style={{ color: '#fff', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>{isAr ? 'حل WIZ' : 'WIZ Solution'}</h3>
                </div>
                <p style={{ color: '#8888aa', lineHeight: 1.8, margin: 0 }}>{study.solution}</p>
              </div>

              {/* Quote */}
              <div style={{
                ...glass, padding: '28px 32px',
                borderLeft: `4px solid ${study.color}`,
                background: `${study.color}08`,
              }}>
                <p style={{ color: '#ccccdd', fontSize: '1.05rem', fontStyle: 'italic', lineHeight: 1.7, marginBottom: 12 }}>
                  "{study.quote}"
                </p>
                <div style={{ color: study.color, fontSize: '13px', fontWeight: 700 }}>
                  — {study.quotePerson}
                </div>
              </div>
            </div>

            {/* Right: features + tech + screenshots */}
            <div className="col-lg-5">
              {/* Screenshots */}
              {study.images.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', gap: 12 }}>
                    {study.images.slice(0, 2).map((img, i) => (
                      <div key={i} style={{
                        flex: 1, height: 200, borderRadius: 16, overflow: 'hidden',
                        border: `1px solid ${study.color}30`,
                      }}>
                        <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Features */}
              <div style={{ ...glass, padding: '28px', marginBottom: 24 }}>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 16, textTransform: 'uppercase' }}>
                  {isAr ? 'المميزات الرئيسية' : 'Key Features'}
                </div>
                {study.features.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: '50%',
                      background: `${study.color}18`, border: `1px solid ${study.color}35`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={study.color} strokeWidth="3">
                        <path d="M5 12l5 5 9-9"/>
                      </svg>
                    </div>
                    <span style={{ color: '#ccccdd', fontSize: '0.9rem' }}>{f}</span>
                  </div>
                ))}
              </div>

              {/* Tech stack */}
              <div style={{ ...glass, padding: '24px' }}>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 14, textTransform: 'uppercase' }}>
                  {isAr ? 'التقنيات المستخدمة' : 'Tech Stack'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {study.technologies.map(t => (
                    <span key={t} style={{
                      fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 20,
                      background: `${study.color}12`, border: `1px solid ${study.color}28`,
                      color: study.color,
                    }}>{t}</span>
                  ))}
                </div>
              </div>

              {/* CTA */}
              {study.link && (
                <a
                  href={study.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    marginTop: 20, padding: '14px',
                    background: `linear-gradient(102deg, ${study.color}cc, ${study.color}88)`,
                    borderRadius: 14, textDecoration: 'none',
                    color: '#fff', fontWeight: 700, fontSize: 14,
                    boxShadow: `0 8px 24px ${study.color}30`,
                  }}
                >
                  {isAr ? 'عرض مباشر' : 'View Live Demo'}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── All 3 Case Studies nav ── */}
      <section style={{ background: 'transparent', padding: '0 0 80px' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <h3 style={{ color: '#fff', fontWeight: 700, fontSize: '1.5rem' }}>{isAr ? 'استعرض جميع قصص النجاح' : 'Explore All Impact Stories'}</h3>
          </div>
          <div className="row g-4">
            {caseStudies.map((cs, i) => (
              <div className="col-md-4" key={cs.id}>
                <div
                  onClick={() => { setActive(i); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  style={{
                    ...glass, padding: '28px', cursor: 'pointer',
                    borderTop: `3px solid ${i === active ? cs.color : 'transparent'}`,
                    transition: 'all 0.3s',
                    opacity: i === active ? 1 : 0.7,
                  }}
                >
                  <div style={{ fontSize: '2rem', marginBottom: 14 }}>{cs.icon}</div>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: cs.color, letterSpacing: 1,
                    textTransform: 'uppercase', marginBottom: 10,
                  }}>
                    {cs.tag}
                  </div>
                  <h4 style={{ color: '#fff', fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.4, marginBottom: 10 }}>
                    {cs.headline}
                  </h4>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {cs.results.slice(0, 2).map((r, j) => (
                      <span key={j} style={{
                        fontSize: 11, fontWeight: 700, color: cs.color,
                        background: `${cs.color}12`, border: `1px solid ${cs.color}25`,
                        padding: '3px 10px', borderRadius: 20,
                      }}>
                        {r.metric} {r.label.split(' ').slice(0, 2).join(' ')}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{
        background: 'rgba(130,88,200,0.06)', backdropFilter: 'blur(20px)',
        padding: '80px 0', textAlign: 'center',
        borderTop: '1px solid rgba(130,88,200,0.15)',
      }}>
        <div className="container">
          <h2 style={{ color: '#fff', marginBottom: 16, fontSize: 'clamp(1.8rem,4vw,2.4rem)', fontWeight: 700 }}>
            {isAr ? 'تريد نتائج مثل هذه؟' : 'Want results like these?'}
          </h2>
          <p style={{ color: '#6b6b88', marginBottom: 32, maxWidth: 460, margin: '0 auto 32px' }}>
            {isAr ? 'كل تحول يبدأ بمحادثة. أخبرنا بتحديك.' : 'Every transformation starts with a conversation. Tell us your challenge.'}
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to={getPath('/contact')} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'linear-gradient(102deg, #8258c8, #2c84c8)',
              color: '#fff', padding: '14px 36px', borderRadius: 50,
              textDecoration: 'none', fontWeight: 700, fontSize: 15,
              boxShadow: '0 8px 28px rgba(130,88,200,0.35)',
            }}>
              {isAr ? 'ابدأ مشروعًا ←' : 'Start a Project →'}
            </Link>
            <Link to={getPath('/projects')} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(14px)',
              color: '#fff', padding: '14px 36px', borderRadius: 50,
              textDecoration: 'none', fontWeight: 700, fontSize: 15,
              border: '1.5px solid rgba(130,88,200,0.45)',
            }}>
              {isAr ? 'عرض جميع المشاريع الـ23' : 'View All 23 Projects'}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default CaseStudies;
